import { BACKEND_URL } from '@/config';
﻿import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import QrCodesModal from '@/components/QrCodesModal';
import QrScanModal from '@/components/QrScanModal';
import SenderFeeSummary, { money } from '@/components/SenderFeeSummary';
import DisputeModal from '@/components/DisputeModal';
import ConfirmDialog from '@/components/ConfirmDialog';
import '@/styles/qr.css';
import '@/styles/mission-detail-v2.css';
import axios from 'axios';
import { cityFlag } from '@/utils/cityFlag';
import { ccySym } from '@/utils/currencyUtils';
import { isQrModalAllowed } from '@/utils/missionGuards';
import {
  AlertTriangle, Archive, ArrowLeft, ArrowLeftRight, ArrowRight, Award, Banknote, Check,
  CircleCheck, Clock, CreditCard, Database, Euro, FileText, Heart, Image, Info, Landmark, Laptop,
  Library, Link, Lock, MessageSquare, Package, PackageCheck, Pill, Plane, Pointer, QrCode,
  Receipt, RefreshCw, Route, Ruler, Salad, ScanLine, ShieldCheck, Shirt, SprayCan, Star, Users, Weight, X,
} from 'lucide-react';
import { euroNetLine, transferLine } from '@/utils/payoutAccount';

const API = BACKEND_URL + '/api';
axios.defaults.withCredentials = true;

const CATEGORY_ICONS = {
  'Documents':FileText, 'Médicaments':Pill,
  'Électronique':Laptop, 'Vêtements':Shirt,
  'Alimentaire':Salad, 'Cosmétiques':SprayCan,
  'Livres':Library,
};

function fmtDate(d) {
  if (!d) return '·';
  return new Date(d).toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
}
function fmtDateShort(d) {
  if (!d) return '·';
  return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' });
}
function daysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round(Math.abs(new Date(b) - new Date(a)) / 86400000);
}
function msnRef(id) { return id ? `#MSN-${String(id).slice(-6).toUpperCase()}` : '·'; }
function annRef(id) { return id ? `#ANN-${String(id).slice(-4).toUpperCase()}` : null; }
function starsStr(n) { return '★'.repeat(Math.max(0, Math.min(5, n))) + '☆'.repeat(5 - Math.max(0, Math.min(5, n))); }
function initials(p) {
  if (!p) return '?';
  return ((p.first_name || '?')[0] + ((p.last_name || '')[0] || '')).toUpperCase();
}

/* ── Timeline steps ────────────────────────────────────────────── */
const TIMELINE_STEPS = [
  { status:'ACCEPTED',          label:'Proposition acceptée · Mission ouverte',  icon:Check },
  { status:'PAYMENT_PENDING',   label:'Paiement en attente',                    icon:CreditCard },
  { status:'RESERVED',          label:'Paiement sécurisé (escrow)',             icon:CreditCard, reservedStep:true },
  { status:'IN_ESCROW',         label:'Fonds en séquestre · Escrow activé',     icon:Lock,        escrowStep:true },
  { status:'PICKUP_VERIFIED',   label:'Pickup validé · QR confirmé',            icon:QrCode,      qrStep:true },
  { status:'IN_TRANSIT',        label:'En transit',                              icon:Plane },
  { status:'DELIVERY_VERIFIED', label:'Livraison validée · QR confirmé',        icon:ScanLine,        qrStep:true },
  { status:'DELIVERED',         label:'Livré',                                   icon:PackageCheck },
  { status:'COMPLETED',         label:'Mission clôturée · Notations enregistrées', icon:Award },
];
const STATUS_ORDER = [
  'PENDING','ACCEPTED','PAYMENT_PENDING','RESERVED','IN_ESCROW',
  'IN_TRANSIT','DELIVERED','COMPLETED',
];

const STATUS_CFG = {
  PENDING:         { label:'En attente',       cls:'msn-status--pending',   ico:Clock },
  ACCEPTED:        { label:'Acceptée',          cls:'msn-status--active',    ico:Check },
  PAYMENT_PENDING: { label:'Paiement dû',       cls:'msn-status--active',    ico:CreditCard },
  RESERVED:        { label:'Réservée',           cls:'msn-status--active',    ico:CreditCard },
  IN_ESCROW:       { label:'Fonds sécurisés',    cls:'msn-status--active',    ico:Lock },
  IN_TRANSIT:      { label:'En transit',          cls:'msn-status--active',    ico:Plane },
  DELIVERED:       { label:'Livrée',              cls:'msn-status--active',    ico:Package },
  COMPLETED:       { label:'Clôturée',            cls:'msn-status--done',      ico:CircleCheck },
  CANCELLED:       { label:'Annulée',             cls:'msn-status--cancelled', ico:X },
  DECLINED:        { label:'Déclinée',            cls:'msn-status--cancelled', ico:X },
  DISPUTED:        { label:'Litige',              cls:'msn-status--error',     ico:AlertTriangle },
};

const DISPUTE_CAT = {
  no_show:'Transporteur absent', damage:'Colis endommagé', lost:'Colis perdu',
  wrong_item:'Mauvais colis', late:'Retard important', no_payment:'Pb paiement',
  fraud:'Fraude suspectée', other:'Autre',
};

/* ═══════════════════════════════════════════════════════════════
   COMPOSANT
   ═══════════════════════════════════════════════════════════════ */
export default function MissionDetailPage() {
  const { id }   = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast    = useToast();

  const [mission, setMission]               = useState(null);
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState('');
  const [showQrCodes, setShowQrCodes]       = useState(false);
  const [showQrScan, setShowQrScan]         = useState(null);
  const [showDispute, setShowDispute]       = useState(false);
  const [showRating, setShowRating]         = useState(false);
  const [cancelPending, setCancelPending]   = useState(false);
  const [rating, setRating]                 = useState({ score: 5, comment: '' });
  const [pollStatus, setPollStatus]         = useState('');
  const [missionRatings, setMissionRatings] = useState([]);
  const pollCancelRef = useRef(false);

  /* ── load ──────────────────────────────────────────────────── */
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/missions/${id}`);
      setMission(data);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mission introuvable');
    } finally {
      setLoading(false);
    }
  };

  const loadRatings = async (msn) => {
    const ratableStatus = msn?.status === 'COMPLETED'
      || (msn?.status === 'CANCELLED' && !!msn.dispute_resolution);
    if (!msn || !ratableStatus) return;
    try {
      const targets = [msn.carrier_id, msn.shipper_id].filter(Boolean);
      const all = [];
      for (const tid of targets) {
        const { data } = await axios.get(`${API}/ratings/user/${tid}`);
        if (Array.isArray(data)) all.push(...data);
      }
      const msnId = String(id);
      setMissionRatings(all.filter(r => String(r.mission_id) === msnId));
    } catch { /* ratings optionnelles */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().then(m => { if (m) loadRatings(m); }); }, [id]);

  /* ── Poll Stripe ───────────────────────────────────────────── */
  useEffect(() => {
    const sp        = new URLSearchParams(window.location.search);
    const sessionId = sp.get('session_id');
    if (!sessionId || sessionId.includes('CHECKOUT_SESSION_ID')) return;
    pollCancelRef.current = false;
    setPollStatus('polling');
    let attempts = 0;
    const MAX = 12, DELAY = 2000;
    const clean = () => window.history.replaceState({}, '', `/app/missions/${id}`);
    const poll = async () => {
      if (pollCancelRef.current) return;
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/v1/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setPollStatus('paid');
          toast.success('✅ Paiement confirmé — fonds en séquestre Stripe.');
          clean(); await load(); return;
        }
        if (attempts < MAX) setTimeout(poll, DELAY);
        else { setPollStatus('failed'); clean(); await load(); toast.info('Vérification du paiement en cours.'); }
      } catch {
        if (attempts < MAX) setTimeout(poll, DELAY);
        else { setPollStatus('failed'); clean(); await load(); }
      }
    };
    poll();
    return () => { pollCancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ── Actions métier ────────────────────────────────────────── */
  const doAction = async (action, extraData = {}) => {
    setActionLoading(action);
    try {
      let url    = `${API}/missions/${id}`;
      let method = 'patch';
      if (action === 'cancel')                url += '/cancel';
      else if (action === 'confirm-delivery') url += '/confirm-delivery';
      else if (action === 'dispute')        { url += '/dispute'; method = 'post'; }
      await axios[method](url, extraData);
      toast.success('Action effectuée.');
      const m = await load();
      if (m) loadRatings(m);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading('');
    }
  };

  const doRating = async () => {
    const targetId = mission.shipper_id === user.id ? mission.carrier_id : mission.shipper_id;
    try {
      await axios.post(`${API}/ratings`, {
        mission_id: id, target_id: targetId,
        score: rating.score, comment: rating.comment,
      });
      toast.success('Merci pour votre évaluation !');
      setShowRating(false);
      const m = await load();
      if (m) loadRatings(m);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  /* ── Loading / erreur ──────────────────────────────────────── */
  if (loading) return (
    <div className="mission-detail-page" style={{ display:'flex', justifyContent:'center', paddingTop:40 }}>
      <div style={{ width:'100%', maxWidth:840 }}>
        <div className="skeleton" style={{ height:120, borderRadius:14, marginBottom:12 }} />
        <div className="skeleton" style={{ height:400, borderRadius:14 }} />
      </div>
    </div>
  );
  if (!mission) return (
    <div className="mission-detail-page">
      <div className="empty-state"><div>Mission introuvable</div></div>
    </div>
  );

  /* ── Dérivations ───────────────────────────────────────────── */
  const isSender    = mission.shipper_id === user?.id;
  const isCarrier   = mission.carrier_id === user?.id;
  const currentIdx  = STATUS_ORDER.indexOf(mission.status);
  const isDisputed  = mission.status === 'DISPUTED';
  const isCancelled = mission.status === 'CANCELLED';
  const isDeclined  = mission.status === 'DECLINED';
  const isCompleted = mission.status === 'COMPLETED';
  const isRatable   = isCompleted || (isCancelled && !!mission.dispute_resolution);
  const isReservedFlow = (
    ['RESERVED','ACCEPTED','IN_TRANSIT','DELIVERED','COMPLETED'].includes(mission.status)
    && mission.payment_intent_id != null
  );
  const disputeDeadline    = mission.confirmed_at
    ? new Date(new Date(mission.confirmed_at).getTime() + 48 * 60 * 60 * 1000) : null;
  const withinDisputeWindow = disputeDeadline && new Date() < disputeDeadline;

  const isStepDone = (step) => {
    if (step.status === 'PICKUP_VERIFIED')   return !!mission.pickup_verified_at;
    if (step.status === 'DELIVERY_VERIFIED') return !!mission.delivery_verified_at;
    if (step.reservedStep  && !isReservedFlow) return false;
    if (step.status === 'PAYMENT_PENDING' && isReservedFlow) return false;
    if (step.status === 'IN_ESCROW'       && isReservedFlow) return false;
    return currentIdx > STATUS_ORDER.indexOf(step.status);
  };
  const isStepActive = (step) => {
    if (step.status === 'PICKUP_VERIFIED')
      return mission.status === 'IN_ESCROW' && !mission.pickup_verified_at;
    if (step.status === 'DELIVERY_VERIFIED')
      return mission.status === 'IN_TRANSIT' && !mission.delivery_verified_at;
    if (step.reservedStep  && !isReservedFlow) return false;
    if (step.status === 'PAYMENT_PENDING' && isReservedFlow) return false;
    if (step.status === 'IN_ESCROW'       && isReservedFlow) return false;
    return STATUS_ORDER.indexOf(step.status) === currentIdx;
  };

  /* ── Données ───────────────────────────────────────────────── */
  const dep         = mission.shipment?.departure_city || '?';
  const arr         = mission.shipment?.arrival_city   || '?';
  const ref         = msnRef(id);
  const aRef        = annRef(mission.shipment?.id);
  const _abbrev = (p) => p
    ? `${p.first_name || ''}${p.last_name ? ' ' + p.last_name[0] + '.' : ''}`.trim() || '·'
    : '·';
  const senderName  = _abbrev(mission.shipper);
  const carrierName = _abbrev(mission.carrier);
  const otherPerson = isSender ? mission.carrier  : mission.shipper;
  const otherName   = isSender ? carrierName : senderName;
  const duration    = daysBetween(
    mission.created_at,
    mission.confirmed_at || mission.delivery_verified_at || mission.updated_at,
  );
  const netAmount = (mission.contracted_price ?? 0) - (mission.platform_commission ?? 0);
  // Net en euros figé à l'encaissement (B11, étape 2) : le transporteur est versé en euros
  const euroNet = euroNetLine(mission.settlement, mission.currency);
  // Versement réel du net (B11, étape 3) : programmé, en attente du compte, versé…
  const transferInfo = transferLine(mission.transfer);
  // commission_rate est figé à la création de la mission ; absent sur les missions plus anciennes
  const commRate  = mission.commission_rate;
  const commPct   = commRate != null
    ? Math.round(commRate * 100)
    : (mission.contracted_price > 0 ? Math.round((mission.platform_commission / mission.contracted_price) * 100) : 8);
  const minApplied = commRate != null
    && mission.platform_commission > commRate * (mission.contracted_price ?? 0) + 0.005;
  const commLabel = minApplied ? `${commPct}% · minimum appliqué` : `${commPct}%`;
  const awaitingPayment = ['ACCEPTED', 'PAYMENT_PENDING'].includes(mission.status);
  const senderFundsNote = (() => {
    if (awaitingPayment) return mission.payment_due_at
      ? `Paiement à effectuer avant le ${fmtDate(mission.payment_due_at)}, sinon la mission sera annulée. Vos fonds seront ensuite bloqués en séquestre Stripe jusqu'à la livraison confirmée.`
      : "Paiement à effectuer. Vos fonds seront ensuite bloqués en séquestre Stripe jusqu'à la livraison confirmée.";
    if (mission.status === 'RESERVED') return "Montant autorisé sur votre carte : il ne sera débité que si le transporteur accepte.";
    if (mission.status === 'CANCELLED' && mission.cancel_reason === 'PAYMENT_DEADLINE') return "Mission annulée : le délai de paiement est dépassé. Aucun montant n'a été débité.";
    if (['CANCELLED', 'DECLINED'].includes(mission.status)) return 'Mission annulée.';
    return "Vos fonds sont bloqués en séquestre Stripe jusqu'à la livraison confirmée.";
  })();
  const statusCfg  = STATUS_CFG[mission.status] || { label: mission.status, cls:'msn-status--pending', ico:Clock };
  const categories = (() => {
    const raw = mission.shipment?.categories ?? [];
    // Si "Autre" coexiste avec d'autres entrées → c'est le placeholder, pas la valeur renseignée
    const filtered = raw.length > 1 ? raw.filter(c => c !== 'Autre') : raw;
    return filtered.length > 0
      ? filtered
      : (mission.shipment?.title ? [mission.shipment.title] : ['Colis']);
  })();

  const stepDate = {
    ACCEPTED:          mission.created_at,
    IN_ESCROW:         mission.escrow_at,
    PICKUP_VERIFIED:   mission.pickup_verified_at,
    DELIVERY_VERIFIED: mission.delivery_verified_at,
    COMPLETED:         mission.confirmed_at,
  };

  /* Ratings chargées */
  // L'auteur d'un avis n'est renvoyé qu'en vue publique (`author.id`, avis confidentiels) : `author_id` n'existe plus,
  // et l'avis déjà donné n'était jamais reconnu (bouton « Évaluer » resté, second envoi refusé)
  const authorOf    = (r) => String(r.author?.id ?? r.author_id);
  const myRating    = missionRatings.find(r => authorOf(r) === String(user?.id));
  const theirRating = missionRatings.find(r => authorOf(r) !== String(user?.id));
  const hasRated    = !!myRating;

  /* Archivage */
  const photoExpiry   = mission.delivery_verified_at
    ? new Date(new Date(mission.delivery_verified_at).getTime() + 14 * 24 * 60 * 60 * 1000) : null;
  const msgExpiry     = mission.confirmed_at
    ? new Date(new Date(mission.confirmed_at).getTime() + 90 * 24 * 60 * 60 * 1000) : null;
  const photoDeleted  = photoExpiry && new Date() > photoExpiry;

  /* Étapes visibles */
  const visibleSteps = TIMELINE_STEPS.filter(step => {
    if (step.qrStep       && !isStepDone(step) && !isStepActive(step)) return false;
    if (step.reservedStep && !isStepDone(step) && !isStepActive(step)) return false;
    return true;
  });

  /* ── Rendu d'une étape ─────────────────────────────────────── */
  const renderStep = (step, idx) => {
    const done   = isStepDone(step);
    const active = isStepActive(step);
    const isLast = idx === visibleSteps.length - 1;
    const dotCls   = done ? 'msn-tl-dot--done'  : active ? 'msn-tl-dot--active'  : 'msn-tl-dot--pending';
    const lineCls  = done ? 'msn-tl-line--done'  : '';
    const titleCls = done ? 'msn-tl-title--done' : active ? 'msn-tl-title--active' : 'msn-tl-title--pending';
    const ts       = stepDate[step.status];

    return (
      <div key={step.status} className="msn-tl-step">
        <div className="msn-tl-left">
          <div className={`msn-tl-dot ${dotCls}`}>
            {done
              ? <Check size={15} aria-hidden="true" />
              : <step.icon size={14} aria-hidden="true" />}
          </div>
          {!isLast && <div className={`msn-tl-line ${lineCls}`} />}
        </div>

        <div className={`msn-tl-body${isLast ? ' msn-tl-body--last' : ''}`}>
          <div className={`msn-tl-title ${titleCls}`}>
            {step.label}
            {active && pollStatus === 'polling' && (
              <RefreshCw size={12} style={{ marginLeft:6, animation:'spin 1s linear infinite', verticalAlign:'middle' }} />
            )}
          </div>

          {done && step.status === 'ACCEPTED' && ts && (
            <div className="msn-tl-time">
              <Clock size={11} aria-hidden="true" /> {fmtDate(ts)}
            </div>
          )}

          {(done || active) && step.escrowStep && (
            <>
              {mission.contracted_price != null && (
                <div className="msn-tl-sub">
                  {mission.contracted_price.toFixed(2)} {ccySym(mission.currency || 'EUR')} bloqués en séquestre Stripe
                  {mission.payment_intent_id && ' · Contrat signé électroniquement'}
                </div>
              )}
              {ts && <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(ts)}</div>}
              {mission.payment_intent_id && (
                <div className="msn-tl-detail">
                  <Lock size={13} style={{ flexShrink:0 }} aria-hidden="true" />
                  Ref. PaymentIntent : {mission.payment_intent_id.slice(0, 24)}…
                </div>
              )}
            </>
          )}

          {done && step.status === 'PICKUP_VERIFIED' && (
            <>
              <div className="msn-tl-sub">
                {isSender
                  ? (carrierName !== '·' ? `${carrierName} a récupéré le colis · ` : '')
                  : 'Vous avez récupéré le colis · '}
                QR oral validé
              </div>
              {ts && <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(ts)}</div>}
              <div className="msn-photos-grid">
                <div className="msn-photo-box">
                  <CircleCheck aria-hidden="true" />
                  <div className="msn-photo-box-title">QR prise en charge</div>
                  <div className="msn-photo-box-sub">Colis remis · état initial</div>
                </div>
                <div className="msn-photo-box">
                  <ShieldCheck aria-hidden="true" />
                  <div className="msn-photo-box-title">Code annulé</div>
                  <div className="msn-photo-box-sub">Consommé à usage unique</div>
                </div>
              </div>
            </>
          )}

          {active && step.status === 'PICKUP_VERIFIED' && (
            <div className="msn-tl-sub msn-tl-sub--active">
              En attente du scan QR de prise en charge
            </div>
          )}

          {done && step.status === 'DELIVERY_VERIFIED' && (
            <>
              <div className="msn-tl-sub">
                QR livraison validé · Stripe capturé
                {netAmount > 0 ? ` · Transfer ${netAmount.toFixed(2)} ${ccySym(mission.currency || 'EUR')} → ${isCarrier ? 'vous' : (carrierName !== '·' ? carrierName : 'transporteur')}` : ''}
              </div>
              {ts && <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(ts)}</div>}
              <div className="msn-photos-grid">
                <div className="msn-photo-box">
                  <CircleCheck aria-hidden="true" />
                  <div className="msn-photo-box-title">QR livraison</div>
                  <div className="msn-photo-box-sub">Remise au destinataire</div>
                </div>
                <div className="msn-photo-box">
                  <Banknote aria-hidden="true" />
                  <div className="msn-photo-box-title">{netAmount.toFixed(2)} {ccySym(mission.currency || 'EUR')} crédités</div>
                  <div className="msn-photo-box-sub">Wallet transporteur</div>
                </div>
              </div>
            </>
          )}

          {active && step.status === 'DELIVERY_VERIFIED' && (
            <div className="msn-tl-sub msn-tl-sub--active">
              En attente du scan QR de livraison
            </div>
          )}

          {done && step.status === 'COMPLETED' && (
            <>
              <div className="msn-tl-sub">
                Notations mutuelles publiées · Affinité enregistrée
              </div>
              {ts && <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(ts)}</div>}
              {mission.payment?.transfer_id && (
                <div className="msn-tl-finance">
                  <ArrowLeftRight size={13} style={{ flexShrink:0 }} aria-hidden="true" />
                  Transfer Stripe : {mission.payment.transfer_id}
                </div>
              )}
            </>
          )}

          {active && !step.qrStep && !step.escrowStep && (
            <div className="msn-tl-sub msn-tl-sub--active">Étape en cours</div>
          )}
        </div>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════════
     RENDU
     ══════════════════════════════════════════════════════════════ */
  return (
    <div className="mission-detail-page">

      {/* Retour */}
      <button className="msn-back" onClick={() => navigate('/app/missions')}>
        <ArrowLeft size={14} /> Mes missions
      </button>

      {/* Poll banner */}
      {pollStatus === 'polling' && (
        <div className="msn-poll-banner">
          <RefreshCw size={15} style={{ animation:'spin 1s linear infinite', flexShrink:0 }} />
          Vérification du paiement Stripe en cours…
        </div>
      )}

      {/* ══ HEADER ════════════════════════════════════════════════ */}
      <div className="msn-header">
        <div className={`msn-header-ico ${
          isCompleted                           ? 'msn-header-ico--done'
          : isDisputed || isCancelled || isDeclined ? 'msn-header-ico--error'
          : 'msn-header-ico--active'
        }`}>
          <statusCfg.ico size={22} aria-hidden="true" />
        </div>

        <div className="msn-header-body">
          <div className="msn-route">
            {cityFlag(dep)} {dep}
            <ArrowRight size={13} color="var(--ink3, #6B7280)" />
            {cityFlag(arr)} {arr}
          </div>
          <div className="msn-meta">
            {ref}
            {otherName !== '·' && <> &nbsp;·&nbsp; {otherName}</>}
            {mission.shipment?.weight_kg && <> &nbsp;·&nbsp; {mission.shipment.weight_kg} kg</>}
            {isCompleted && mission.confirmed_at && (
              <> &nbsp;·&nbsp; Clôturée le {fmtDateShort(mission.confirmed_at)}</>
            )}
            {!isCompleted && mission.delivery_verified_at && (
              <> &nbsp;·&nbsp; Livré le {fmtDateShort(mission.delivery_verified_at)}</>
            )}
          </div>
          <div className="msn-tags">
            {aRef && (
              <span className="msn-tag msn-tag--blue">
                <Link size={10} aria-hidden="true" /> Annonce {aRef}
              </span>
            )}
            {isCompleted && (
              <span className="msn-tag msn-tag--teal">
                <CircleCheck size={10} aria-hidden="true" /> Mission clôturée
              </span>
            )}
            {isCompleted && (
              <span className="msn-tag msn-tag--teal">
                <Heart size={10} aria-hidden="true" /> Affinité enregistrée
              </span>
            )}
            {!isCompleted && (
              <span className={`msn-tag ${
                isDisputed || isCancelled || isDeclined ? 'msn-tag--red'
                : ['PENDING'].includes(mission.status) ? 'msn-tag--gray'
                : 'msn-tag--gold'
              }`}>
                <statusCfg.ico size={10} aria-hidden="true" /> {statusCfg.label}
              </span>
            )}
          </div>
        </div>

        <div className="msn-header-right">
          <div className={`msn-status-badge ${statusCfg.cls}`}>
            <statusCfg.ico size={14} aria-hidden="true" />
            {statusCfg.label}
          </div>
          {mission.contracted_price != null && (
            <div style={{
              fontSize: 18, fontWeight: 700, marginTop: 6,
              color: 'var(--ink, #111827)',
              fontFamily: 'var(--f-display)',
            }}>
              {mission.contracted_price.toFixed(2)} {ccySym(mission.currency || 'EUR')}
            </div>
          )}
        </div>
      </div>

      {/* ══ LAYOUT 2 COLONNES ════════════════════════════════════ */}
      <div className="msn-layout">

        {/* ── COLONNE PRINCIPALE ──────────────────────────────────── */}
        <div>

          {/* Litige banner */}
          {isDisputed && (
            <div className="msn-dispute-banner">
              <AlertTriangle className="msn-dispute-icon" size={18} />
              <div style={{ flex:1, minWidth:0 }}>
                <div className="msn-dispute-title">Litige ouvert</div>
                {mission.dispute?.category && (
                  <div className="msn-dispute-cat">
                    Catégorie : <strong>{DISPUTE_CAT[mission.dispute.category] || mission.dispute.category}</strong>
                  </div>
                )}
                <div className="msn-dispute-msg">{mission.dispute?.message || mission.dispute_message}</div>
                {Array.isArray(mission.dispute?.photos) && mission.dispute.photos.length > 0 && (
                  <div className="msn-dispute-photos">
                    {mission.dispute.photos.map((p, i) => (
                      <img key={i} src={p} alt={`preuve-${i + 1}`} loading="lazy" />
                    ))}
                  </div>
                )}
                <div className="msn-dispute-meta">L'équipe Colis2Ship intervient sous 72h.</div>
              </div>
            </div>
          )}

          {/* ── Timeline ─────────────────────────────────────────── */}
          <div className="msn-card">
            <div className="msn-card-title">
              <Route aria-hidden="true" /> Historique de la mission
            </div>

            {isCancelled && (
              <div className="msn-tl-step">
                <div className="msn-tl-left">
                  <div className="msn-tl-dot msn-tl-dot--error"><X size={15} /></div>
                </div>
                <div className="msn-tl-body msn-tl-body--last">
                  <div className="msn-tl-title">Mission annulée</div>
                  <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(mission.updated_at)}</div>
                </div>
              </div>
            )}

            {isDeclined && (
              <div className="msn-tl-step">
                <div className="msn-tl-left">
                  <div className="msn-tl-dot msn-tl-dot--error"><X size={15} /></div>
                </div>
                <div className="msn-tl-body msn-tl-body--last">
                  <div className="msn-tl-title">Mission déclinée</div>
                  <div className="msn-tl-sub">Le transporteur a refusé la demande</div>
                  <div className="msn-tl-time"><Clock size={11} aria-hidden="true" /> {fmtDate(mission.updated_at)}</div>
                </div>
              </div>
            )}

            {!isCancelled && !isDeclined && visibleSteps.map((step, idx) =>
              renderStep(step, idx)
            )}
          </div>

          {/* ── Notations mutuelles ───────────────────────────────── */}
          {isRatable && (
            <div className="msn-card">
              <div className="msn-card-title">
                <Star aria-hidden="true" /> Notations mutuelles
              </div>

              {/* Ma notation */}
              {myRating ? (
                <>
                  <div className="msn-nota-lbl">
                    Votre notation pour {otherName}
                  </div>
                  <div className="msn-nota-card">
                    <div className="msn-nota-head">
                      <div className={`msn-av ${isSender ? 'msn-av--blue' : 'msn-av--teal'}`}>
                        {initials(otherPerson)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:500, color:'var(--ink,#111827)', fontFamily:'var(--font-body)' }}>
                          {otherPerson ? `${otherPerson.first_name} ${otherPerson.last_name}` : otherName}
                        </div>
                        <div style={{ fontSize:12, color:'var(--ink3,#6B7280)', fontFamily:'var(--font-body)' }}>
                          {isSender ? 'Transporteur' : 'Expéditeur'}
                        </div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div className="msn-nota-stars">{starsStr(myRating.score)}</div>
                        <div style={{ fontSize:12, color:'var(--ink3,#6B7280)', fontFamily:'var(--font-body)' }}>
                          {myRating.score}.0 / 5
                        </div>
                      </div>
                    </div>
                    {myRating.comment && (
                      <p className="msn-nota-txt">"{myRating.comment}"</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="msn-nota-lbl">Votre notation pour {otherName}</div>
                  <div className="msn-nota-card" style={{ textAlign:'center', padding:'20px 12px' }}>
                    <div style={{ fontSize:13, color:'var(--ink3,#6B7280)', marginBottom:12, fontFamily:'var(--font-body)' }}>
                      Partagez votre expérience avec {otherName}
                    </div>
                    <button className="btn btn-gold" onClick={() => setShowRating(true)}>
                      <Star size={14} /> Laisser une évaluation
                    </button>
                  </div>
                </>
              )}

              {/* Notation reçue */}
              {theirRating && (
                <>
                  <div className="msn-nota-lbl" style={{ marginTop: myRating ? 4 : 0 }}>
                    Notation reçue de {isSender ? carrierName : senderName}
                  </div>
                  <div className="msn-nota-card">
                    <div className="msn-nota-head">
                      <div className={`msn-av ${isSender ? 'msn-av--teal' : 'msn-av--blue'}`}>
                        {initials(user)}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:14, fontWeight:500, color:'var(--ink,#111827)', fontFamily:'var(--font-body)' }}>
                          {user?.first_name} {user?.last_name}
                        </div>
                        <div style={{ fontSize:12, color:'var(--ink3,#6B7280)', fontFamily:'var(--font-body)' }}>
                          {isSender ? 'Expéditeur' : 'Transporteur'} · vous
                        </div>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div className="msn-nota-stars">{starsStr(theirRating.score)}</div>
                        <div style={{ fontSize:12, color:'var(--ink3,#6B7280)', fontFamily:'var(--font-body)' }}>
                          {theirRating.score}.0 / 5
                        </div>
                      </div>
                    </div>
                    {theirRating.comment && (
                      <p className="msn-nota-txt">"{theirRating.comment}"</p>
                    )}
                  </div>
                </>
              )}

              {/* Double 5/5 */}
              {myRating?.score === 5 && theirRating?.score === 5 && (
                <div className="msn-affinity-banner">
                  <Heart aria-hidden="true" />
                  <span>
                    Double 5/5 — affinité historique activée entre vous et {otherName}.
                    Vous bénéficierez d'un bonus <strong>+10 pts</strong> dans tous les prochains matchings.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Actions ──────────────────────────────────────────── */}
          {!isCancelled && !isDeclined && !isDisputed && (
            <div className="msn-card">
              <div className="msn-card-title">
                <Pointer aria-hidden="true" /> Actions
              </div>

              {isSender && ['ACCEPTED','PAYMENT_PENDING'].includes(mission.status) && (mission.contracted_price ?? 0) < 1 && (
                <div className="msn-danger-banner">
                  <AlertTriangle size={14} style={{ flexShrink:0 }} />
                  Montant {(mission.contracted_price ?? 0).toFixed(2)} € insuffisant (min 1,00 €).
                </div>
              )}

              {isCarrier && mission.status === 'IN_ESCROW' && !mission.pickup_verified_at && (
                <div className="msn-warn">
                  <AlertTriangle size={14} className="msn-warn-icon" />
                  <span className="msn-warn-txt">
                    <strong>Étape obligatoire :</strong> demandez à l'expéditeur d'afficher son QR de remise puis scannez-le.
                  </span>
                </div>
              )}

              {isCarrier && mission.status === 'IN_TRANSIT' && !mission.delivery_verified_at && (
                <div className="msn-warn">
                  <AlertTriangle size={14} className="msn-warn-icon" />
                  <span className="msn-warn-txt">
                    <strong>Étape obligatoire :</strong> demandez au destinataire d'afficher son QR de livraison puis scannez-le.
                  </span>
                </div>
              )}

              {isCompleted && isSender && withinDisputeWindow && (
                <div className="msn-warn" style={{ marginBottom:10 }}>
                  <AlertTriangle size={14} className="msn-warn-icon" />
                  <div className="msn-warn-txt">
                    <strong>Fenêtre de litige ouverte.</strong> Contestez jusqu'au{' '}
                    <strong>{disputeDeadline.toLocaleString('fr-FR', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</strong>.
                  </div>
                </div>
              )}

              {isCompleted && isSender && !withinDisputeWindow && (
                <div className="msn-info-banner">
                  <Info size={14} style={{ flexShrink:0 }} aria-hidden="true" />
                  La fenêtre de litige post-livraison (48h) est expirée.
                </div>
              )}

              <div className="msn-actions">
                {/* Sender : payer — prix + frais figés à la création de la mission (C8) */}
                {isSender && ['ACCEPTED','PAYMENT_PENDING'].includes(mission.status) && (
                  <button data-testid="btn-pay-mission" className="btn btn-gold"
                    disabled={actionLoading === 'pay' || (mission.contracted_price ?? 0) < 1}
                    onClick={async () => {
                      setActionLoading('pay');
                      try {
                        const { data } = await axios.post(`${API}/payments/v1/checkout/session`, {
                          mission_id: id, origin_url: window.location.origin,
                        });
                        window.location.href = data.url;
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Erreur paiement');
                        setActionLoading('');
                      }
                    }}>
                    <CreditCard size={16} />
                    {actionLoading === 'pay'
                      ? 'Redirection Stripe…'
                      : `Payer ${money(mission.total_charged ?? mission.contracted_price ?? 0, mission.currency || 'EUR')}`}
                  </button>
                )}
                {isSender && awaitingPayment && mission.payment_due_at && (
                  <span className="msn-pay-deadline" data-testid="pay-deadline">
                    À payer avant le {fmtDate(mission.payment_due_at)}
                  </span>
                )}

                {isSender && mission.status === 'PAYMENT_PENDING' && pollStatus !== 'polling' && (
                  <button className="btn btn-ghost btn-sm"
                    onClick={async () => { await load(); toast.info('Page mise à jour.'); }}>
                    <RefreshCw size={14} /> Vérifier le statut
                  </button>
                )}

                {isSender && ['IN_ESCROW','IN_TRANSIT','DELIVERED'].includes(mission.status) && (
                  <button data-testid="btn-view-qr" className="btn btn-primary"
                    onClick={() => setShowQrCodes(true)}>
                    <QrCode size={16} /> Voir mes QR codes
                  </button>
                )}

                {isCarrier && mission.status === 'IN_ESCROW' && !mission.pickup_verified_at && (
                  <button data-testid="btn-scan-pickup" className="btn btn-primary"
                    onClick={() => setShowQrScan('pickup')}>
                    <ScanLine size={16} /> Scanner le QR de prise en charge
                  </button>
                )}

                {isCarrier && mission.status === 'IN_TRANSIT' && !mission.delivery_verified_at && (
                  <button data-testid="btn-scan-delivery" className="btn btn-gold"
                    onClick={() => setShowQrScan('delivery')}>
                    <ScanLine size={16} /> Scanner le QR de livraison
                  </button>
                )}

                {isSender && mission.status === 'DELIVERED' && (
                  <button data-testid="btn-confirm-delivery" className="btn btn-teal"
                    disabled={!!actionLoading}
                    onClick={() => doAction('confirm-delivery')}>
                    <Check size={16} />
                    {actionLoading === 'confirm-delivery' ? '…' : 'Confirmer & libérer les fonds'}
                  </button>
                )}

                {['IN_ESCROW','IN_TRANSIT','DELIVERED'].includes(mission.status) && !isCarrier && (
                  <button data-testid="btn-open-dispute" className="btn btn-danger btn-sm"
                    onClick={() => setShowDispute(true)}>
                    <AlertTriangle size={14} /> Ouvrir un litige
                  </button>
                )}

                {isCarrier && ['IN_ESCROW','IN_TRANSIT'].includes(mission.status) && (
                  <button data-testid="btn-open-dispute-carrier" className="btn btn-danger btn-sm"
                    onClick={() => setShowDispute(true)}>
                    <AlertTriangle size={14} /> Ouvrir un litige
                  </button>
                )}

                {isCompleted && isSender && withinDisputeWindow && (
                  <button data-testid="btn-open-dispute-completed" className="btn btn-danger btn-sm"
                    onClick={() => setShowDispute(true)}>
                    <AlertTriangle size={14} /> Ouvrir un litige
                  </button>
                )}

                {!['IN_ESCROW','IN_TRANSIT','DELIVERED','COMPLETED','DECLINED'].includes(mission.status) && !isCompleted && (
                  <button className="btn btn-ghost btn-sm"
                    disabled={!!actionLoading}
                    onClick={() => setCancelPending(true)}>
                    <X size={14} /> Annuler
                  </button>
                )}
              </div>
            </div>
          )}

        </div>

        {/* ── SIDEBAR ─────────────────────────────────────────────── */}
        <div className="msn-sidebar">

          {/* Informations */}
          <div className="msn-card">
            <div className="msn-card-title">
              <Info aria-hidden="true" /> Informations
            </div>
            <div className="msn-info-row">
              <span className="msn-info-lbl">Référence</span>
              <span className="msn-info-val">{ref}</span>
            </div>
            {aRef && (
              <div className="msn-info-row">
                <span className="msn-info-lbl">Annonce</span>
                <span className="msn-info-val">{aRef}</span>
              </div>
            )}
            <div className="msn-info-row">
              <span className="msn-info-lbl">Corridor</span>
              <span className="msn-info-val">{dep} → {arr}</span>
            </div>
            {mission.carrier && (
              <div className="msn-info-row">
                <span className="msn-info-lbl">Transporteur</span>
                <span className="msn-info-val">{carrierName}</span>
              </div>
            )}
            <div className="msn-info-row">
              <span className="msn-info-lbl">Créée le</span>
              <span className="msn-info-val">{fmtDateShort(mission.created_at)}</span>
            </div>
            {mission.pickup_verified_at && (
              <div className="msn-info-row">
                <span className="msn-info-lbl">Pickup</span>
                <span className="msn-info-val">{fmtDateShort(mission.pickup_verified_at)} · {dep}</span>
              </div>
            )}
            {(mission.delivery_verified_at || mission.confirmed_at) && (
              <div className="msn-info-row">
                <span className="msn-info-lbl">Livraison</span>
                <span className="msn-info-val">
                  {fmtDateShort(mission.delivery_verified_at || mission.confirmed_at)} · {arr}
                </span>
              </div>
            )}
            {duration != null && (
              <div className="msn-info-row">
                <span className="msn-info-lbl">Durée totale</span>
                <span className="msn-info-val">{duration} jour{duration > 1 ? 's' : ''}</span>
              </div>
            )}
          </div>

          {/* Parties */}
          <div className="msn-card">
            <div className="msn-card-title">
              <Users aria-hidden="true" /> Parties
            </div>
            {[
              { person: mission.shipper, role:'Expéditeur',   av:'msn-av--teal', isMe: isSender },
              { person: mission.carrier, role:'Transporteur', av:'msn-av--blue', isMe: isCarrier },
            ].map(({ person, role, av, isMe }) => person && (
              <div key={role} className="msn-party-row">
                <div className={`msn-av ${av}`}>{initials(person)}</div>
                <div style={{ flex:1 }}>
                  <div className="msn-party-name">{person.first_name} {person.last_name}</div>
                  <div className="msn-party-sub">
                    {role}
                    {person.avg_rating != null && (
                      <> &nbsp;·&nbsp; <span className="msn-stars">
                        {'★'.repeat(Math.round(person.avg_rating))}
                      </span> {person.avg_rating.toFixed(1)}</>
                    )}
                  </div>
                </div>
                {isMe && (
                  <span className="msn-tag msn-tag--teal" style={{ fontSize:10 }}>Vous</span>
                )}
              </div>
            ))}
          </div>

          {/* Colis */}
          {mission.shipment && (
            <div className="msn-card">
              <div className="msn-card-title">
                <Package aria-hidden="true" /> Colis {isCompleted ? 'livré' : ''}
              </div>
              {categories.map((cat, i) => {
                const CategoryIcon = CATEGORY_ICONS[cat] || Package;
                return (
                  <div key={i} className="msn-pkg-row">
                    <div className="msn-pkg-ico" style={{ background:'#E6F1FB', color:'#0C447C' }}>
                      <CategoryIcon size={16} aria-hidden="true" />
                    </div>
                    <div className="msn-pkg-name">{cat}</div>
                  </div>
                );
              })}
              {/* Poids + dimensions : une seule fois pour l'envoi global */}
              {(mission.shipment.weight_kg || mission.shipment.dimensions) && (
                <div style={{
                  display:'flex', alignItems:'center', gap:16,
                  paddingTop:10, marginTop:2,
                  borderTop:'0.5px solid var(--c-bd)',
                  fontSize:12, color:'var(--c-ink3)', fontFamily:'var(--font-body)',
                }}>
                  {mission.shipment.weight_kg && (
                    <span><Weight size={13} style={{ marginRight:4, verticalAlign:'middle' }} aria-hidden="true" />{mission.shipment.weight_kg} kg</span>
                  )}
                  {mission.shipment.dimensions && (
                    <span><Ruler size={13} style={{ marginRight:4, verticalAlign:'middle' }} aria-hidden="true" />{mission.shipment.dimensions}</span>
                  )}
                </div>
              )}
              {mission.shipment.description && (
                <div style={{
                  fontSize:12, color:'var(--ink3,#6B7280)', marginTop:8, paddingTop:8,
                  borderTop:'0.5px solid var(--border,rgba(17,24,39,.09))',
                  lineHeight:1.5, fontFamily:'var(--font-body)',
                }}>
                  {mission.shipment.description.slice(0, 120)}
                  {mission.shipment.description.length > 120 ? '…' : ''}
                </div>
              )}
            </div>
          )}

          {/* Finances */}
          {mission.contracted_price != null && (
            <div className="msn-card">
              <div className="msn-card-title">
                <Banknote aria-hidden="true" /> Récapitulatif financier
              </div>
              {(() => { const sym = ccySym(mission.currency || 'EUR'); return (<>
              {isSender ? (<>
                {/* Frais Colis2Ship figés dans la mission (C8) : prix, frais (ou « offerts »), puis ce qui est débité */}
                {mission.sender_fee_listed > 0 ? (<>
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl">Prix du transport</span>
                    <span>{money(mission.contracted_price, mission.currency || 'EUR')}</span>
                  </div>
                  <SenderFeeSummary fee={mission} totalLabel={awaitingPayment ? 'À payer' : 'Montant payé'} />
                </>) : (
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl">{awaitingPayment ? 'À payer' : 'Montant payé'}</span>
                    <span data-testid="mission-paid">{money(mission.total_charged ?? mission.contracted_price, mission.currency || 'EUR')}</span>
                  </div>
                )}
                {mission.payment?.escrow_at && (
                  <RouterLink to={`/app/missions/${id}/recu`} className="msn-receipt-link" data-testid="mission-receipt-link">
                    <Receipt size={14} aria-hidden="true" /> Voir le reçu
                  </RouterLink>
                )}
                {isCompleted && (<>
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl">Crédité au transporteur</span>
                    <span className="msn-fin-val--pos">{netAmount.toFixed(2)} {sym}</span>
                  </div>
                  {mission.confirmed_at && (
                    <div className="msn-fin-row">
                      <span className="msn-fin-lbl">Crédité le</span>
                      <span style={{ fontSize:12 }}>{fmtDateShort(mission.confirmed_at)}</span>
                    </div>
                  )}
                  {mission.payment?.transfer_id && (
                    <div className="msn-fin-row">
                      <span className="msn-fin-lbl">Ref. Stripe</span>
                      <span className="msn-fin-val--mono">{mission.payment.transfer_id.slice(0,16)}…</span>
                    </div>
                  )}
                  {mission.platform_commission != null && (
                    <div className="msn-fin-note">
                      <Info aria-hidden="true" />
                      Commission plateforme ({commLabel} · {mission.platform_commission.toFixed(2)} {sym}) déduite du virement transporteur.
                    </div>
                  )}
                </>)}
                {!isCompleted && (
                  <div className="msn-fin-note" data-testid="sender-funds-note">
                    {awaitingPayment ? <Clock aria-hidden="true" /> : <Lock aria-hidden="true" />}
                    {senderFundsNote}
                    {mission.platform_commission != null && !['CANCELLED', 'DECLINED'].includes(mission.status)
                      && ` La commission (${mission.platform_commission.toFixed(2)} ${sym} · ${commLabel}) est à la charge du transporteur.`}
                  </div>
                )}
              </>) : (<>
                <div className="msn-fin-row">
                  <span className="msn-fin-lbl">Montant de la mission</span>
                  <span>{mission.contracted_price.toFixed(2)} {sym}</span>
                </div>
                {mission.platform_commission != null && (
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl" style={{ display:'flex', alignItems:'center', gap:6 }}>
                      Commission
                      {commPct <= 12 && (
                        <span className={`msn-fin-badge ${commPct <= 8 ? 'msn-fin-badge--pro' : 'msn-fin-badge--std'}`}>
                          {commPct <= 8 ? 'Taux réduit' : 'Standard'} · {commPct}%
                        </span>
                      )}
                      {minApplied && (
                        <span className="msn-fin-badge msn-fin-badge--std" title="Le montant minimum de commission s'applique à cette mission">
                          Minimum
                        </span>
                      )}
                    </span>
                    <span className="msn-fin-val--neg">−{mission.platform_commission.toFixed(2)} {sym}</span>
                  </div>
                )}
                {isCompleted && mission.confirmed_at && (
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl">Crédité le</span>
                    <span style={{ fontSize:12 }}>{fmtDateShort(mission.confirmed_at)}</span>
                  </div>
                )}
                {isCompleted && mission.payment?.transfer_id && (
                  <div className="msn-fin-row">
                    <span className="msn-fin-lbl">Ref. Stripe</span>
                    <span className="msn-fin-val--mono">{mission.payment.transfer_id.slice(0,16)}…</span>
                  </div>
                )}
                <div className="msn-fin-total">
                  <span>{isCompleted
                    ? (isCarrier ? 'Votre net reçu' : `Net ${carrierName !== '·' ? carrierName : 'transporteur'}`)
                    : (isCarrier ? 'Vous recevez' : `${carrierName !== '·' ? carrierName : 'Transporteur'} recevra`)
                  }</span>
                  <span className="msn-fin-val--pos">{netAmount.toFixed(2)} {sym}</span>
                </div>
                {isCarrier && euroNet && (
                  <div className="msn-fin-note" data-testid="carrier-net-eur" data-exact={euroNet.exact}>
                    <Euro size={14} aria-hidden="true" />
                    {euroNet.label}. {euroNet.note}
                  </div>
                )}
                {isCarrier && transferInfo && (
                  <div className="msn-fin-note" data-testid="carrier-transfer" data-status={mission.transfer.status}>
                    <Landmark size={14} aria-hidden="true" />
                    <span>
                      {transferInfo.label}
                      {transferInfo.link && <> <a href={transferInfo.link}>Configurer mes versements</a></>}
                    </span>
                  </div>
                )}
                {awaitingPayment && mission.payment_due_at && (
                  <div className="msn-fin-note" data-testid="carrier-payment-note">
                    <Clock aria-hidden="true" />
                    En attente du paiement de l'expéditeur, au plus tard le {fmtDate(mission.payment_due_at)}. Sans paiement, la mission sera annulée et la capacité de votre trajet restituée.
                  </div>
                )}
                {!isCompleted && (
                  <div className="msn-fin-note">
                    <Info aria-hidden="true" />
                    Votre net vous est versé 48 h après la confirmation de livraison, à la fin du délai de recours.
                  </div>
                )}
              </>)}
              </>); })()}
            </div>
          )}

          {/* Archivage */}
          {(isCompleted || isCancelled) && (
            <div className="msn-card">
              <div className="msn-card-title">
                <Archive aria-hidden="true" /> Archivage
              </div>
              {photoExpiry && (
                <div className="msn-archive-item">
                  <Image style={{ color: photoDeleted ? 'var(--red,#dc2626)' : 'var(--ink3,#6B7280)' }} aria-hidden="true" />
                  <span>Photos</span>
                  <span className="msn-archive-item-end"
                    style={{ color: photoDeleted ? 'var(--red,#dc2626)' : 'var(--ink3,#6B7280)' }}>
                    {photoDeleted
                      ? `Supprimées le ${fmtDateShort(photoExpiry)}`
                      : `Jusqu'au ${fmtDateShort(photoExpiry)}`}
                  </span>
                </div>
              )}
              {msgExpiry && (
                <div className="msn-archive-item">
                  <MessageSquare style={{ color:'var(--ink3,#6B7280)' }} aria-hidden="true" />
                  <span>Messages</span>
                  <span className="msn-archive-item-end">Jusqu'au {fmtDateShort(msgExpiry)}</span>
                </div>
              )}
              <div className="msn-archive-item">
                <FileText style={{ color:'#085041' }} aria-hidden="true" />
                <span>Contrat PDF</span>
                <span className="msn-archive-item-end" style={{ color:'#085041' }}>5 ans</span>
              </div>
              <div className="msn-archive-item">
                <Database style={{ color:'#085041' }} aria-hidden="true" />
                <span>Données mission</span>
                <span className="msn-archive-item-end" style={{ color:'#085041' }}>5 ans</span>
              </div>
            </div>
          )}

          {/* Boutons rapides */}
          <button className="msn-action-btn" onClick={() => navigate('/app/messages')}>
            <MessageSquare size={15} aria-hidden="true" /> Messagerie
          </button>
          {isRatable && !hasRated && (
            <button className="msn-action-btn" onClick={() => setShowRating(true)} data-testid="btn-open-rating">
              <Star size={14} /> Évaluer {isSender ? 'le transporteur' : "l'expéditeur"}
            </button>
          )}
          {(isCancelled || isCompleted) && mission.shipment && isSender && (
            <button className="msn-action-btn"
              onClick={() => navigate(`/app/shipments/new?republish=${mission.shipment.id}`)}>
              <RefreshCw size={15} aria-hidden="true" /> Republier cette annonce
            </button>
          )}

        </div>
      </div>

      {/* ══ MODALES ════════════════════════════════════════════════ */}
      <DisputeModal open={showDispute} onClose={() => setShowDispute(false)}
        missionId={id} mission={mission} onSuccess={() => load()} />

      <ConfirmDialog
        open={cancelPending}
        onClose={() => setCancelPending(false)}
        onConfirm={() => { setCancelPending(false); doAction('cancel'); }}
        variant="destructive"
        icon="trash"
        title="Annuler la mission ?"
        subtitle="Cette action est irréversible. La mission sera annulée."
        confirmLabel="Confirmer l'annulation"
        busy={!!actionLoading}
      />

      {showQrCodes && isQrModalAllowed(mission?.status, 'codes') && (
        <QrCodesModal missionId={id} onClose={() => setShowQrCodes(false)} />
      )}
      {showQrScan === 'pickup' && isQrModalAllowed(mission?.status, 'pickup') && (
        <QrScanModal step="pickup"
          onClose={() => setShowQrScan(null)}
          onSuccess={() => { setShowQrScan(null); load(); }} />
      )}
      {showQrScan === 'delivery' && isQrModalAllowed(mission?.status, 'delivery') && (
        <QrScanModal step="delivery"
          onClose={() => setShowQrScan(null)}
          onSuccess={() => { setShowQrScan(null); load(); }} />
      )}

      {/* Modal évaluation */}
      {showRating && (
        <div className="modal-backdrop">
          <div className="modal-box" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <div className="modal-title">
                Évaluer {isSender ? 'le transporteur' : "l'expéditeur"} — {otherName}
              </div>
              <button className="modal-close" onClick={() => setShowRating(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="msn-rating-stars">
                {[1,2,3,4,5].map(s => (
                  <button key={s} type="button"
                    className={`msn-star${s <= rating.score ? ' msn-star--filled' : ''}`}
                    onClick={() => setRating(r => ({ ...r, score:s }))}>★</button>
                ))}
              </div>
              <textarea className="field-textarea"
                placeholder="Commentaire (optionnel)…"
                value={rating.comment}
                onChange={e => setRating(r => ({ ...r, comment: e.target.value }))}
                rows={3} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowRating(false)}>Plus tard</button>
              <button data-testid="btn-submit-rating" className="btn btn-gold" onClick={doRating}>
                Publier l'avis
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
